// src/hooks/usePaginatedQuery.ts
import { updatePaginationState, usePaginationState } from "@/store/paginationStore";
import * as Apollo from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { calculateAmountToFetch, shouldFetchMore, shouldFetchNext } from "../utils/pagination";

// Hook for getting all necessary pagination functions
export function usePaginatedQuery<TEdge, TData, TVariables extends Apollo.OperationVariables>
(options: UseGeneratedQueryOptions<TData, TVariables>, paginationName: keyof PaginationState, reversePagination = false, fetchPolicy?: Apollo.WatchQueryFetchPolicy, initialFetchPolicy?: Apollo.WatchQueryFetchPolicy)
    : UsePaginatedQueryResult<TEdge, TVariables> {
  const { queryDocument } = options;
  const paginationData = usePaginationState(paginationName);
  // Track if this is the first time loading this specific pagination state
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Determine if this is a fresh load (first page) or returning to a saved state
  const isFreshLoad = paginationData.currentPage === 0;

  // Construct initial variables with proper sorting
  const initialVariables = {
    ...paginationData,
    after: null  // Always start with null, let Apollo handle the rest
  } as unknown as TVariables;

  // Use network-only only for fresh loads on first page, otherwise use cache-first
  // This ensures we get fresh data on first visit, but use cached data when returning
  const effectiveFetchPolicy = (!hasLoadedOnce && isFreshLoad)
    ? (initialFetchPolicy || "network-only")
    : (fetchPolicy || "cache-first");

  const { data, loading, fetchMore, subscribeToMore } = useQuery<TData, TVariables>(queryDocument, {
    variables: initialVariables,
    fetchPolicy: effectiveFetchPolicy,
  });

  // Mark as loaded once when we get data and we're on the first page
  useEffect(() => {
    if (data && isFreshLoad && !hasLoadedOnce) {
      setHasLoadedOnce(true);
    }
  }, [data, isFreshLoad, hasLoadedOnce]);

  // Reset hasLoadedOnce when pagination state changes significantly
  useEffect(() => {
    if (paginationData.currentPage === 0) {
      setHasLoadedOnce(false);
    }
  }, [paginationData.currentPage]);

  const [ loadingMoreData, setLoadingMoreData ] = useState(false);

  const getMaxPageVisited = (data: FetchMoreResult<typeof paginationName>) => {
    if(!data) return 0;
    const displayAmount = paginationData.first;
    return Math.ceil(data[paginationName].edges.length / displayAmount) - 1;
  };

  /**
   * This function handles a next page click
   */
  const handleNextPage = () => {
    // Useful constants
    const currentData = data as FetchMoreResult<typeof paginationName>;
    const maxPageVisited = getMaxPageVisited(currentData);
    const hasNextPage = currentData[paginationName].pageInfo.hasNextPage;
    const currentPage = paginationData.currentPage;

    // Return if we are on the last page
    if (!hasNextPage && currentPage >= maxPageVisited) return;

    // Update current Page
    updatePaginationState({[paginationName]: { currentPage: currentPage + 1 } });

    // Check if we should fetch more data
    const should = shouldFetchNext(maxPageVisited, currentPage, hasNextPage);

    // Return if we should not fetch more data
    if (!should) return;

    // Store the endCursor
    const endCursor = currentData[paginationName].pageInfo.endCursor;

    // Run query update logic
    setLoadingMoreData(true);
    fetchMore({
      variables: {
        ...paginationData,
        after: endCursor,
      } as unknown as Partial<TVariables>,
      updateQuery: (previousResult, { fetchMoreResult }) => {
        setLoadingMoreData(false);
        if (!fetchMoreResult) return previousResult;
        const result = fetchMoreResult as unknown as FetchMoreResult<typeof paginationName>;
        const prevResult = previousResult as unknown as FetchMoreResult<typeof paginationName>;

        return {
          [paginationName]: {
            __typename: prevResult[paginationName].__typename,
            edges: [
              ...prevResult[paginationName].edges,
              ...result[paginationName].edges,
            ],
            pageInfo: result[paginationName].pageInfo,
          }
        } as TData;
      }
    });
  };

  /**
   * This function handles a previous page click
   */
  const handlePreviousPage = () => {
    const currentPage = paginationData.currentPage;
    if (currentPage === 0) { return; }
    updatePaginationState({[paginationName]: { currentPage: currentPage - 1 } });
  };

  /**
   * This function handles a display amount change
   * @param newAmount the new display amount
   */
  const handleDisplayAmount = (newAmount: number) => {
    const displayAmount = paginationData.first;
    if(newAmount == displayAmount) return;
    // Useful constants
    const currentData = data as FetchMoreResult<typeof paginationName>;
    const length = currentData[paginationName].edges.length;
    const difference = calculateAmountToFetch(newAmount, length, displayAmount);
    const hasNextPage = currentData[paginationName].pageInfo.hasNextPage;

    setLoadingMoreData(false);

    // Check if we should fetch more data
    const should = shouldFetchMore(difference, hasNextPage);

    updatePaginationState({[paginationName]: { first: newAmount, currentPage: 0 } });

    // Return if we should not fetch more data
    if (!should) return;

    const endCursor = currentData[paginationName].pageInfo.endCursor;

    // Should not fetch more data
    setLoadingMoreData(true);
    fetchMore({
      variables: {
        ...paginationData,
        first: difference,
        after: endCursor,
      } as unknown as Partial<TVariables>,
      updateQuery: (previousResult, { fetchMoreResult }) => {
        setLoadingMoreData(false);
        if (!fetchMoreResult) return previousResult;
        const result = fetchMoreResult as unknown as FetchMoreResult<typeof paginationName>;
        const prevResult = previousResult as unknown as FetchMoreResult<typeof paginationName>;

        // Dispatch reset pagination action

        const newEdges = result[paginationName].edges;
        if (newEdges.length === 0) {
          // Return if newEdges is an empty array
          return previousResult;
        }

        return {
          [paginationName]: {
            __typename: prevResult[paginationName].__typename,
            edges: [
              ...prevResult[paginationName].edges,
              ...newEdges,
            ],
            pageInfo: result[paginationName].pageInfo,
          }
        } as TData;
      }
    });
  };

  /**
   * This function handles a filter submit
   * @param variables filters to apply
   * needs to be optional because of the ref used
   */
  const handleFilterSubmit = (variables: any) => {
    updatePaginationState({ [paginationName]: { currentPage: 0 } });
    setHasLoadedOnce(false);
    setLoadingMoreData(true);
    fetchMore({
      variables: {
        // Explicitly preserve filters when applying new filters
        ...paginationData,
        ...variables,
        first: paginationData.first,
        after: null, // Use null instead of 0 or "" for relay-style pagination
      } as unknown as TVariables,
      updateQuery: (previousResult: any, { fetchMoreResult }: any) => {
        setLoadingMoreData(false);
        if (!fetchMoreResult) return previousResult;
        const result = fetchMoreResult as FetchMoreResult<typeof paginationName>;
        const prevResult = previousResult as FetchMoreResult<typeof paginationName>;

        const newEdges = result[paginationName].edges;

        return {
          [paginationName]: {
            __typename: prevResult[paginationName].__typename,
            edges: [
              ...newEdges,
            ],
            pageInfo: result[paginationName].pageInfo,
          }
        };
      }
    });
  };

  /**
   * This function slices the data based on the currentPage and displayAmount
   * @param data the data to slice
   * @returns the sliced data
   */
  const slicePaginatedResult = (data: TData) => {
    if(!data) return [];
    const displayAmount = paginationData.first;
    const currentPage = paginationData.currentPage;

    // Slice the data based on the currentPage and displayAmount
    const start = currentPage * displayAmount;
    const end = start + displayAmount;
    const dataSlice = (data as unknown as FetchMoreResult<typeof paginationName>)[paginationName].edges.slice(start, end);
    return reversePagination ? dataSlice.reverse() as TEdge[] : dataSlice as TEdge[];
  };

  const getAmountRowsFetched = () => {
    if(!data) return 0;
    return (data as unknown as FetchMoreResult<typeof paginationName>)[paginationName].edges.length;
  };

  const getShowingFrom = () => {
    const currentPage = paginationData.currentPage;
    const displayAmount = paginationData.first;
    return currentPage === 0 ? 1 : currentPage * displayAmount + 1;
  };

  const getShowingTo = () => {
    const currentPage = paginationData.currentPage;
    const displayAmount = paginationData.first;
    return currentPage === getMaxPageVisited(data as unknown as FetchMoreResult<typeof paginationName>) ?
      getAmountRowsFetched()
      :
      (currentPage + 1) * displayAmount;
  };

  return {
    handleNextPage: reversePagination ? handlePreviousPage : handleNextPage,
    handlePreviousPage: reversePagination ? handleNextPage : handlePreviousPage,
    handleDisplayAmount,
    handleFilterSubmit,
    loading,
    loadingMoreData,
    displayAmount: paginationData.first,
    dataSlice: slicePaginatedResult(data as TData),
    subscribeToMore,
    showingFrom: getShowingFrom(),
    showingTo: getShowingTo(),
    rowsFetched: getAmountRowsFetched(),
  };
}
